import { FC } from 'react'
import {
  AssetSize,
  ContentList,
  ContentListItem,
  Divider,
  DividerLine,
  Heading,
  HeadingLevel,
  HeadingTag,
  Icon,
  IconButton,
  IconButtonFunction,
  IconName,
  Link,
  LinkFunction,
  LinkSize,
  LinkTarget,
  LinkVariant,
  Tabs,
  Tooltip,
  TooltipPlacement,
  TabsItem,
  TabsOrientation,
  TabsPanel
} from '@enbw/ui-react'
import { useTranslation } from 'react-i18next'
import styles from './styles.module.scss'
import { MEK_URI } from '@/constants'
import { formatDate, FormattedDateMode } from '@/util/date-util'
import { valueOrEmpty } from '@/util/string-util'
import { CustomerMasterData } from '@/domain/contract/types/customer'

type FlyoutItem = {
  label: string
  value: string
  fullRow?: boolean
}

type FlyoutSection = {
  title: string
  items: FlyoutItem[]
}

type ContractDetailsFlyoutProps = {
  customer: CustomerMasterData
  onClose: () => void
}

export const ContractDetailsFlyout: FC<ContractDetailsFlyoutProps> = ({ customer, onClose }) => {
  const { t } = useTranslation()
  const mekUrl = customer.myEnergyKey.authSubject ? `${MEK_URI}/auth0-delegated-admin/users/${customer.myEnergyKey.authSubject}` : undefined

  const deviceInfoSection: FlyoutSection = {
    title: t('contract.customerFlyout.deviceInfo.title'),
    items: [
      { label: t('contract.customerFlyout.deviceInfo.fields.createdAt'), value: formatDate(customer.deviceData.createdAt, FormattedDateMode.DateAndTime) },
      { label: t('contract.customerFlyout.deviceInfo.fields.lastUsedAt'), value: formatDate(customer.deviceData.lastUsedAt, FormattedDateMode.DateAndTime) },
      { label: t('contract.customerFlyout.deviceInfo.fields.osName'), value: valueOrEmpty(customer.deviceData.osName) },
      { label: t('contract.customerFlyout.deviceInfo.fields.osVersion'), value: valueOrEmpty(customer.deviceData.osVersion) },
      { label: t('contract.customerFlyout.deviceInfo.fields.deviceName'), value: valueOrEmpty(customer.deviceData.deviceName) },
      { label: t('contract.customerFlyout.deviceInfo.fields.appVersion'), value: valueOrEmpty(customer.deviceData.appVersion) }
    ]
  }

  return (
    <div className={styles.flyoutCard}>
      <div className={styles.flyoutHeader}>
        <Heading level={HeadingLevel.H3} tag={HeadingTag.H3}>
          {t('contract.customerFlyout.title')}
        </Heading>

        <IconButton name={IconName.CloseLarge} function={IconButtonFunction.Tertiary} onClick={onClose}></IconButton>
      </div>

      <div className={styles.flyoutContent}>
        <Tabs id="customer-flyout-tabs" orientation={TabsOrientation.Horizontal}>
          <TabsItem slot="items" label={t('contract.customerFlyout.tabs.general')} active={true}></TabsItem>

          <TabsItem slot="items" label={t('contract.customerFlyout.tabs.deviceApp')}></TabsItem>

          <TabsPanel slot="panels" active={true}>
            <div className={styles.tabSection}>
              <Heading level={HeadingLevel.H6} tag={HeadingTag.H6}>
                {t('contract.customerFlyout.general.enbwMobilityPlus')}
              </Heading>
              <div className={styles.sectionBox}>
                <div className={styles.sectionBoxGrid}>
                  <ContentListItem label={t('contract.customerFlyout.keyFigures.fields.mobilityPlusId')} className={styles.flyoutItemFullRow}>
                    <div slot={'body'} title={valueOrEmpty(customer.mobilityPlusId.mobilityPlusId)} className={styles.flyoutOverflowLine}>
                      {valueOrEmpty(customer.mobilityPlusId.mobilityPlusId)}
                    </div>
                  </ContentListItem>
                </div>
                <Divider line={DividerLine.Subtle} />
                <div className={styles.sectionBoxGrid}>
                  <Heading level={HeadingLevel.H6} tag={HeadingTag.H6} className={styles.flyoutItemFullRow}>
                    {t('contract.customerFlyout.contracts.title')}
                  </Heading>
                  <ContentListItem
                    label={t('contract.customerFlyout.contracts.fields.activeContracts')}
                    body={customer.contractCounts.activeContracts.toString()}
                  />
                  <ContentListItem
                    label={t('contract.customerFlyout.contracts.fields.contractsInTermination')}
                    body={customer.contractCounts.contractsInTermination.toString()}
                  />
                  <ContentListItem
                    label={t('contract.customerFlyout.contracts.fields.terminatedContracts')}
                    body={customer.contractCounts.terminatedContracts.toString()}
                  />
                </div>
                <Divider line={DividerLine.Subtle} />
                <div className={styles.sectionBoxGrid}>
                  <Heading level={HeadingLevel.H6} tag={HeadingTag.H6} className={styles.flyoutItemFullRow}>
                    {t('contract.customerFlyout.fleetMemberships.title')}
                  </Heading>
                  <ContentListItem
                    label={t('contract.customerFlyout.fleetMemberships.fields.activeMemberships')}
                    body={customer.fleetMembershipCounts.activeMemberships.toString()}
                  />
                  <ContentListItem
                    label={t('contract.customerFlyout.fleetMemberships.fields.previousMemberships')}
                    body={customer.fleetMembershipCounts.previousMemberships.toString()}
                  />
                </div>
              </div>

              <Heading level={HeadingLevel.H6} tag={HeadingTag.H6}>
                {t('contract.customerFlyout.general.enbw')}
              </Heading>
              <div className={styles.sectionBox}>
                <div className={styles.sectionBoxGrid}>
                  <div className={styles.flyoutSectionHeader}>
                    <Heading level={HeadingLevel.H6} tag={HeadingTag.H6} className={styles.flyoutItemFullRow}>
                      {t('contract.customerFlyout.myEnergyKey.title')}
                    </Heading>
                    <Tooltip placement={TooltipPlacement.Left}>
                      <Link
                        slot="trigger"
                        href={mekUrl}
                        function={LinkFunction.Secondary}
                        variant={LinkVariant.Title}
                        size={LinkSize.Small}
                        target={LinkTarget.Blank}
                        disabled={!mekUrl}
                        data-testid="mek-link"
                      >
                        <Icon slot="trailing-icon" name={IconName.OpenInNew} size={AssetSize.XSmall} />
                      </Link>
                      <span slot="content">{t('contract.customerFlyout.myEnergyKey.description')}</span>
                    </Tooltip>
                  </div>
                  <ContentListItem
                    label={t('contract.customerFlyout.myEnergyKey.subjectId')}
                    body={valueOrEmpty(customer.myEnergyKey.authSubject)}
                    className={styles.flyoutItemFullRow}
                  />
                  <ContentListItem label={t('contract.customerFlyout.myEnergyKey.emailAddress')} className={styles.flyoutItemFullRow}>
                    <div slot={'body'} title={valueOrEmpty(customer.myEnergyKey.mekEmailAddress)} className={styles.flyoutOverflowLine}>
                      {valueOrEmpty(customer.myEnergyKey.mekEmailAddress)}
                    </div>
                  </ContentListItem>
                </div>
              </div>
            </div>
          </TabsPanel>

          <TabsPanel slot="panels">
            <div className={styles.tabSection}>
              <Heading level={HeadingLevel.H6} tag={HeadingTag.H6}>
                {t('contract.customerFlyout.deviceInfo.title')}
              </Heading>
              <div className={styles.sectionBox}>
                <ContentList>
                  <div className={styles.sectionBoxGrid}>
                    {deviceInfoSection.items.map((item) => (
                      <div key={`${deviceInfoSection.title}-${item.label}`} className={item.fullRow ? styles.flyoutItemFullRow : undefined}>
                        <ContentListItem label={item.label} body={item.value} />
                      </div>
                    ))}
                  </div>
                </ContentList>
              </div>
            </div>
          </TabsPanel>
        </Tabs>
      </div>
    </div>
  )
}
